import { Option } from './option.model';

/**
 * Brings a company's stored lead-source option labels up to date with the code catalogue.
 *
 * The option list is saved per company in the flow config, so a company that saved before a rename
 * still holds the old label - in `text` AND in `_i18n`, which is read first. Renaming the catalogue
 * alone therefore changes nothing for existing companies, in any language.
 *
 * Only labels that still match the superseded default are replaced. A company that wrote its own
 * wording keeps it: we cannot ask the config whether a label was customised, but a value that is
 * byte-identical to the default it shipped with demonstrably was not.
 *
 * Options saved before the catalogue carried defaults keys have neither a key nor `_i18n`, so they
 * also adopt the catalogue's key - without it getOptionLabel cannot reach the built-in translation
 * and shows the English text in every language.
 */
export class LeadSourceOptionLabels {
  /**
   * Labels superseded by the LLS-530 rename, keyed by the option's defaults key. `''` is the base
   * `text` field (English); other keys are `_i18n` language codes.
   */
  static readonly SUPERSEDED: Record<string, Record<string, string>> = {
    'leadSource.pastClients': {
      '': 'Past Clients',
      es: 'Clientes anteriores',
    },
  };

  static refreshStoredLabels = (storedOptions: Option[], catalogue: Option[]): void => {
    (storedOptions ?? []).forEach((storedOption) => {
      // Matched on `value`, the stable key: `text` is what we are about to change and `name` may
      // legitimately differ from it.
      const current = (catalogue ?? []).find((option) => option.value === storedOption.value);

      if (!current?._defaultsKey) {
        return;
      }

      if (!storedOption._defaultsKey) {
        storedOption._defaultsKey = current._defaultsKey;
      }

      const superseded = LeadSourceOptionLabels.SUPERSEDED[current._defaultsKey];

      if (!superseded) {
        return;
      }

      if (superseded[''] !== undefined && storedOption.text === superseded['']) {
        storedOption.text = current.text;
      }

      Object.keys(superseded)
        .filter((lang) => lang !== '')
        .forEach((lang) => {
          const storedLabel = storedOption._i18n?.[lang]?.['text'];
          const currentLabel = current._i18n?.[lang]?.['text'];

          if (storedLabel === superseded[lang] && currentLabel) {
            storedOption._i18n[lang]['text'] = currentLabel;
          }
        });
    });
  };
}
